// Vetrina pubblica — "Case in vendita" (portale immobiliare). Elenco immobili pubblicati.
import React from 'react';
import { Link } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { realEstateService } from '@/services/realEstate.service';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { ExperiencesCrossSell } from '@/components/public/ExperiencesCrossSell';
import { Seo } from '@/components/Seo';
import { Skeleton } from '@/components/ui/skeleton';
import { Home, MapPin, Maximize, BedDouble, Zap, ArrowRight, ImageIcon, Building2 } from 'lucide-react';

const HERO = 'https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920';
const euro = (n) => (n == null ? 'Prezzo su richiesta' : `€${Number(n).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`);

function ListingCard({ l }) {
  return (
    <Link to={`/case-in-vendita/${l.id}`} className="group block overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" data-testid={`re-pub-card-${l.id}`}>
      <div className="relative h-56 overflow-hidden bg-muted">
        {l.photos?.[0]?.url ? (
          <img src={mediaUrl(l.photos[0].url)} alt={l.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-9 w-9 text-muted-foreground" /></div>
        )}
        <span className="absolute right-3 top-3 rounded-full bg-emerald-700 px-3 py-1 text-sm font-semibold text-white">{euro(l.price)}</span>
      </div>
      <div className="p-5">
        <h3 className="font-display text-xl font-semibold text-foreground">{l.title}</h3>
        {(l.city || l.province) && <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin className="h-4 w-4 text-primary" />{[l.city, l.province].filter(Boolean).join(' · ')}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-3 text-sm text-foreground">
          {l.sqm != null && <span className="flex items-center gap-1.5"><Maximize className="h-4 w-4 text-primary" />{l.sqm} m²</span>}
          {l.rooms != null && <span className="flex items-center gap-1.5"><BedDouble className="h-4 w-4 text-primary" />{l.rooms} locali</span>}
          {l.energy_class && <span className="flex items-center gap-1.5"><Zap className="h-4 w-4 text-primary" />Classe {l.energy_class}</span>}
          <span className="ml-auto flex items-center gap-1 font-medium text-primary">Dettagli <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
        </div>
      </div>
    </Link>
  );
}

export default function CaseInVendita() {
  const { data, isLoading } = useApiQuery(['public', 'real-estate'], () => realEstateService.publicList(), {
    select: (d) => d?.listings || [],
  });
  const items = data || [];

  return (
    <PublicLayout>
      <Seo title="Case in vendita — Sila Experiences" description="Immobili selezionati in vendita nel territorio della Sila." image={HERO} />
      <SilaHero image={HERO} badge="Immobiliare" title="Case in vendita in Sila" subtitle="Immobili selezionati nel cuore del Parco. Contatta l’amministrazione per informazioni e visite." icon={Home} />

      <section className="sila-section py-10">
        <h2 className="font-display text-2xl font-bold tracking-tight text-foreground">Gli immobili</h2>
        <p className="mt-1 text-sm text-muted-foreground">La vendita è gestita direttamente dall’amministrazione di Sila Experiences.</p>

        <div className="mt-6">
          {isLoading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-80 w-full rounded-2xl" />)}</div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="re-pub-empty">
              <Building2 className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-3 font-medium text-foreground">Nessun immobile disponibile al momento</p>
              <p className="text-sm text-muted-foreground">Torna presto: i nuovi immobili appariranno qui.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="re-pub-grid">
              {items.map((l) => <ListingCard key={l.id} l={l} />)}
            </div>
          )}
        </div>
      </section>

      <ExperiencesCrossSell title="Mentre valuti casa, scopri la Sila" subtitle="Le esperienze che renderanno unica la tua vita nel Parco." />
    </PublicLayout>
  );
}
